import * as React from "react";
import { useState } from "react";
import {
  Menu,
  MenuButton,
  MenuList,
  type MenuOpenChangeData,
  MenuPopover,
  MenuTrigger,
  slot,
  useMergedRefs,
  useOverflowMenu,
} from "@fluentui/react-components";
import { AppIconFlight as AppIcon } from "@1js/acui-icons";
import type {
  AppTabListOverflowMenuProps,
  AppTabListOverflowMenuState,
} from "./AppTabListOverflowMenu.types";
import type { OnExecuteParameter } from "@1js/acui-common";
import { useAppComponentKeytip, deriveTabKeytip } from "@1js/acui-keytip";
import { useAppComponentTooltips } from "@1js/acui-tooltip";
import { pivotOverflowButtonId } from "@1js/acui-pivot";
import { useMenuStyles } from "./useAppTabListOverflowMenuStyles.styles";
import { AppContext } from "@1js/acui-context";

export const useAppTabListOverflowMenu = (
  props: AppTabListOverflowMenuProps,
  ref: React.Ref<HTMLButtonElement>
): AppTabListOverflowMenuState => {
  const {
    ariaLabel,
    topItems,
    overflowItems,
    overflowTabHeaderMenuLauncherIcon,
    onMenuButtonClick,
    onMenuItemClick,
    onOverflowButtonExecute,
    keytipProps,
    onOverflowTabSelect,
  } = props;
  const { ref: overflowRef, isOverflowing } =
    useOverflowMenu<HTMLButtonElement>();
  const appContext = React.useContext(AppContext);

  const [menuOpened, setMenuOpened] = useState(false);

  const menuStyles = useMenuStyles();

  const handleMenuButtonClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    setMenuOpened(!menuOpened);
    onMenuButtonClick(event);
  };

  const onExecute = React.useCallback(
    (param: OnExecuteParameter) => {
      setMenuOpened(!menuOpened);
      if (onOverflowButtonExecute) {
        onOverflowButtonExecute(param);
      }
    },
    [onOverflowButtonExecute, menuOpened, setMenuOpened]
  );

  // Callback for when a tab is selected from the overflow menu
  const onOverflowTabExecuted = React.useCallback(
    (tabId: string) => {
      setMenuOpened(!menuOpened);
      onOverflowTabSelect?.(tabId);
    },
    [onOverflowTabSelect, setMenuOpened, menuOpened]
  );

  if (overflowItems?.findIndex((item) => item === undefined) >= 0) {
    appContext?.logger?.onMessage(
      `TabList Overflow attempting to render an undefined item`
    );
  }

  const overflowTabs = overflowItems?.map((header) => ({
    ...header,
    onOverflowTabSelect: onOverflowTabExecuted,
  }));

  // Calculate final ref to use with tooltips and keytips
  const { mergedRef } = useAppComponentTooltips(ref, {
    label: ariaLabel,
    hideLabel: true,
  });

  const mergedRefWithKeytip = useAppComponentKeytip(
    {
      id: pivotOverflowButtonId,
      onExecute,
    },
    mergedRef,
    "AppButton",
    deriveTabKeytip,
    {
      keytipProps: keytipProps
        ? {
            ...keytipProps,
            hasMenu: true,
          }
        : undefined,
      directClickRef: overflowRef,
      useDirectClick: true,
    }
  );

  /** In order for the menu to dismiss when we want it to with
   * things like keytips, we need to keep track of the state of
   * the menu open. This function just gets the callback from
   * the base fluent menu and sets the state
   */
  const onMenuOpenChange = (
    _e:
      | MouseEvent
      | React.MouseEvent<HTMLElement, MouseEvent>
      | React.KeyboardEvent<HTMLElement>
      | TouchEvent,
    data: MenuOpenChangeData
  ) => {
    setMenuOpened(data.open);
  };

  const root = slot.always(props.root, {
    defaultProps: {
      hasIcons: false,
      mountNode: { className: menuStyles.portal },
      open: menuOpened,
      onOpenChange: onMenuOpenChange,
    },
    elementType: Menu,
  });

  // @ts-ignore -- Remove when https://github.com/microsoft/fluentui/issues/29578 is fixed
  const menuTrigger = slot.always(props.menuTrigger, {
    defaultProps: { disableButtonEnhancement: true },
    elementType: MenuTrigger,
  });

  const triggerIcon = slot.always(
    props.triggerIcon ?? {
      children: <AppIcon iconName={overflowTabHeaderMenuLauncherIcon} />,
    },
    { elementType: "span" }
  );

  const trigger = slot.always(props.trigger, {
    defaultProps: {
      id: pivotOverflowButtonId,
      type: "button",
      "aria-label": ariaLabel,
      appearance: "subtle",
      ref: useMergedRefs<HTMLElement | null | undefined>(
        overflowRef,
        mergedRefWithKeytip
      ),
      size: "small",
      onClick: handleMenuButtonClick,
      menuIcon: triggerIcon,
    },
    elementType: MenuButton,
  });

  const menuPopover = slot.always(props.menuPopover, {
    elementType: MenuPopover,
  });
  const menuList = slot.always(props.menuList, { elementType: MenuList });
  const menuListWrapper = slot.always(props.menuListWrapper, {
    elementType: "div",
  });

  return {
    isOverflowing,
    topItems,
    overflowItems: overflowTabs,
    onMenuItemClick,

    components: {
      root: Menu,
      menuTrigger: MenuTrigger,
      trigger: MenuButton,
      triggerIcon: "span",
      menuPopover: MenuPopover,
      menuList: MenuList,
      menuListWrapper: "div",
    },

    root,
    // @ts-ignore -- Remove when https://github.com/microsoft/fluentui/issues/29578 is fixed
    menuTrigger,
    trigger,
    triggerIcon,
    menuPopover,
    menuList,
    menuListWrapper,
    menuOpened,
  };
};
